import { legalPages, type LegalPageContent, type StaticPageSlug } from "./legalContent";
import type { TemplateId, WallFace } from "./domain/design";

export type Language = "ja" | "en";
export type PageSlug = StaticPageSlug | "contact";

type CategoryCopy = Record<TemplateId, { title: string; summary: string }>;
type WallFaceCopy = Record<WallFace, string>;

export type AppCopy = {
  brand: {
    name: string;
    tagline: string;
    logoLabel: string;
  };
  languageSwitch: {
    ariaLabel: string;
    ja: string;
    en: string;
  };
  seo: {
    defaultTitle: string;
    defaultDescription: string;
    contactTitle: string;
    contactDescription: string;
    privacyFallbackDescription: string;
    termsFallbackDescription: string;
    commercialDisclosureDescription: string;
    ogImageAlt: string;
  };
  notices: {
    templateLoaded: (templateTitle: string) => string;
    checkoutUrlMissing: string;
    pendingDesignMissing: string;
    savedDesignMismatch: string;
    paymentDesignMismatch: string;
    paymentSuccess: string;
    checkoutCanceled: string;
    checkoutSessionMissing: string;
    saveDesignFailed: string;
    deviceDesignSaved: (name: string) => string;
    deviceDesignOpened: (name: string) => string;
    deviceDesignDeleted: (name: string) => string;
    deviceDesignMissing: string;
    deviceSaveFailed: string;
    restoreDesignSuccess: string;
    restoreDesignFileMissing: string;
    reexportFilesMissing: string;
    reexportSuccess: string;
    nativeExportSuccess: string;
    exportUpgradePurchaseSuccess: string;
    exportUpgradePurchaseCanceled: string;
    exportUpgradePurchasePending: string;
    exportUpgradeStatusFailed: string;
    exportUpgradeRestored: string;
    exportUpgradeRestoreMissing: string;
    entitlementInvalid: string;
    entitlementExpired: string;
    entitlementDesignTypeMismatch: string;
    reexportDesignMismatch: string;
    genericError: string;
  };
  landing: {
    heroDescription: string;
    cta: string;
    capabilitiesAriaLabel: string;
    capabilities: string[];
    howToTitle: string;
    howToSteps: Array<{ title: string; summary: string }>;
    useCaseTitle: string;
    useCaseBody: string;
    useCases: Array<{ title: string; summary: string }>;
    pricingTitle: string;
    pricingBody: string;
    pricingItems: Array<{ term: string; description: string }>;
    faqTitle: string;
    faqs: Array<{ question: string; answer: string }>;
  };
  footer: {
    ariaLabel: string;
    links: Record<PageSlug, string>;
  };
  categories: {
    backLabel: string;
    title: string;
    createLabel: (categoryTitle: string) => string;
    items: CategoryCopy;
  };
  editor: {
    aboutApp: string;
    backToCategoriesLabel: string;
    hideToolbar: string;
    showToolbar: string;
    checkout: {
      creating: string;
      verifying: string;
      upgrading: string;
      exporting: string;
      exportReady: string;
      download: string;
      upgrade: string;
      reexport: string;
      reexporting: string;
      restorePurchase: string;
      restoringPurchase: string;
    };
    restoreDesign: string;
    saveDesign: string;
    savedDesigns: string;
    savedDesignsTitle: string;
    closeSavedDesigns: string;
    noSavedDesigns: string;
    openSavedDesign: string;
    openSavedDesignLabel: (name: string) => string;
    deleteSavedDesign: string;
    deleteSavedDesignLabel: (name: string) => string;
    deleteSavedDesignConfirm: (name: string) => string;
    savedDesignLimitWarning: (name: string) => string;
    sections: {
      size: string;
      basics: string;
      add: string;
    };
    fields: {
      width: string;
      depth: string;
      height: string;
      bottom: string;
      wallThickness: string;
      bottomThickness: string;
      cornerRadius: string;
      handleSize: string;
      dividerThickness: string;
      dividerHeight: string;
    };
    bottomOptions: {
      withBottom: string;
      withoutBottom: string;
    };
    tools: {
      handle: string;
      verticalDivider: string;
      horizontalDivider: string;
    };
    splitSelection: string;
    deleteSelection: string;
    toolHelp: {
      handleDesktop: string;
      dividerDesktop: string;
      mobile: string;
    };
    mobileSelection: {
      dividerAriaLabel: string;
      handleAriaLabel: string;
      face: string;
      horizontalPosition: string;
      depthPosition: string;
      height: string;
      left: string;
      right: string;
      back: string;
      front: string;
      bottom: string;
      top: string;
    };
    handleControls: {
      label: string;
      none: string;
      notch: string;
      slot: string;
    };
    wallFaceLabels: WallFaceCopy;
    addToolAriaLabel: (label: string) => string;
    handleSideDrop: {
      previousFace: string;
      nextFace: string;
      currentFace: (faceLabel: string) => string;
    };
    modelHints: {
      handleDesktop: (faceLabel: string) => string;
      dividerDesktop: string;
      mobile: string;
    };
    validation: {
      fixBeforeDownload: string;
      range: (min: string, max: string, unit: string) => string;
      required: string;
    };
  };
  contact: {
    title: string;
    lead: string;
    note: string;
    mailSubject: string;
    mailButton: string;
  };
  numberField: {
    decrease: (label: string) => string;
    increase: (label: string) => string;
  };
};

const jaCopy: AppCopy = {
  brand: {
    name: "ハコフィット",
    tagline: "3Dプリントデータ作成",
    logoLabel: "ハコフィット"
  },
  languageSwitch: {
    ariaLabel: "表示言語",
    ja: "日本語",
    en: "EN"
  },
  seo: {
    defaultTitle: "ハコフィット | ぴったりサイズの収納パーツ用3Dプリントデータ作成",
    defaultDescription: "ハコフィットは、引き出し・棚・ケースに合わせた収納パーツ用3Dプリントデータをブラウザで作れるWebアプリです。",
    contactTitle: "お問い合わせ | ハコフィット",
    contactDescription: "ハコフィットへのご質問、ご要望、不具合報告、決済やダウンロードに関するお問い合わせ先です。",
    privacyFallbackDescription: "ハコフィットにおけるユーザー情報の取扱いを定めるページです。",
    termsFallbackDescription: "ハコフィットの提供条件および利用に関する権利義務関係を定めるページです。",
    commercialDisclosureDescription: "ハコフィットの販売業者、販売価格、支払方法、返品・キャンセル条件など、特定商取引法に基づく表記です。",
    ogImageAlt: "ハコフィットのサービス紹介画像"
  },
  notices: {
    templateLoaded: (templateTitle) => `${templateTitle}の初期値を読み込みました。`,
    checkoutUrlMissing: "Checkout URLが取得できませんでした。",
    pendingDesignMissing: "支払いは戻りましたが、決済前の設計データが見つかりません。アプリの出力ボタンからもう一度開始してください。",
    savedDesignMismatch: "保存された設計データの確認に失敗しました。もう一度エクスポートしてください。",
    paymentDesignMismatch: "支払い情報と設計データが一致しません。",
    paymentSuccess: "支払い確認が完了しました。再出力キー入りのZIPをダウンロードしました。",
    checkoutCanceled: "決済をキャンセルしました。編集内容は復元されています。",
    checkoutSessionMissing: "Checkout Session IDが見つかりません。",
    saveDesignFailed: "ブラウザに設計データを一時保存できませんでした。",
    deviceDesignSaved: (name) => `${name}をこの端末に保存しました。`,
    deviceDesignOpened: (name) => `${name}を開きました。`,
    deviceDesignDeleted: (name) => `${name}を削除しました。`,
    deviceDesignMissing: "保存済みの設計が見つかりませんでした。",
    deviceSaveFailed: "この端末に設計を保存できませんでした。",
    restoreDesignSuccess: "design.jsonから設計を復元しました。",
    restoreDesignFileMissing: "design.jsonを選択してください。",
    reexportFilesMissing: "再出力にはdesign.jsonとhakofit-key.jsonを両方選択してください。",
    reexportSuccess: "再出力キーを確認し、同じ設計データを再ダウンロードしました。",
    nativeExportSuccess: "ZIPを作成しました。共有先を選んで保存してください。",
    exportUpgradePurchaseSuccess: "出力アップグレードが有効になりました。",
    exportUpgradePurchaseCanceled: "アップグレード購入をキャンセルしました。",
    exportUpgradePurchasePending: "購入は保留中です。承認後にもう一度出力してください。",
    exportUpgradeStatusFailed: "購入状態を確認できませんでした。通信状態を確認してもう一度お試しください。",
    exportUpgradeRestored: "購入済みの出力アップグレードを復元しました。",
    exportUpgradeRestoreMissing: "復元できる出力アップグレードが見つかりませんでした。",
    entitlementInvalid: "再出力キーの署名を確認できませんでした。",
    entitlementExpired: "再出力キーの有効期限が切れています。",
    entitlementDesignTypeMismatch: "再出力キーの設計タイプが一致しません。",
    reexportDesignMismatch: "design.jsonと再出力キーが一致しません。同じZIPに入っていたファイルを選択してください。",
    genericError: "処理に失敗しました。時間を置いてもう一度お試しください。"
  },
  landing: {
    heroDescription: "引き出し・棚などに完璧にフィットする収納グッズを、簡単作業で3Dプリント用データに。",
    cta: "ワンクリックで始める",
    capabilitiesAriaLabel: "できること",
    capabilities: ["登録不要", "編集・プレビュー無料", "プリントは外注でOK"],
    howToTitle: "作ってから出力",
    howToSteps: [
      { title: "① まずエディターで作る", summary: "サイズを入力し、仕切りや取手を動かして形を確認" },
      { title: "② 気に入ったら出力", summary: "必要な設計だけ3Dプリントデータとしてダウンロード" }
    ],
    useCaseTitle: "収納場所に合わせて、まず形を作成",
    useCaseBody: "ハコフィットは一般的な3D CADではなく、箱・トレー・仕切り付き収納パーツに特化したWebアプリです。\n購入前にサイズ感や仕切りの位置を試し、使えそうな形になってから出力できます。",
    useCases: [
      {
        title: "引き出し・棚に合うボックス",
        summary: "幅・奥行・高さを入力して、既製品では実現できないピッタリフィットの収納箱を作れます。"
      },
      {
        title: "趣味・日用品の整理",
        summary: "文房具、ガジェット、コスメ、薬、ケーブル、工具、推し活グッズ、ボードゲーム、カード類など、さまざまな物に合わせた箱を作れます。"
      }
    ],
    pricingTitle: "編集・プレビューは無料。出力は税込550円",
    pricingBody: "編集・3Dプレビューまでは無料です。料金が発生するのは、作った設計を3Dプリントデータとしてダウンロードする時だけです。",
    pricingItems: [
      { term: "編集・プレビュー", description: "無料" },
      { term: "データ出力", description: "3Dプリントデータ出力料 550円" },
      { term: "外部プリント", description: "造形費・送料は各サービスで確認" },
      { term: "配送物", description: "完成品配送ではなくデータ提供" }
    ],
    faqTitle: "よくある質問",
    faqs: [
      {
        question: "試すだけでも料金はかかりますか？",
        answer: "いいえ。エディターでサイズや仕切りを調整し、3Dプレビューで形を確認するところまでは無料です。料金がかかるのは、3Dプリントデータをダウンロードする時だけです。"
      },
      {
        question: "完成品の収納用品が届きますか？",
        answer: "いいえ。ハコフィットは完成品を販売・配送するサービスではありません。収納パーツ用の3Dプリントデータを作成します。"
      },
      {
        question: "3Dプリンターを持っていなくても使えますか？",
        answer: "使えます。出力した3DプリントデータをDMM.makeやJLC3DPなどの外部3Dプリントサービスに入稿すれば印刷することができます。"
      },
      {
        question: "どんなデータを出力できますか？",
        answer: "箱型のものを自由なサイズで作れます。取手と仕切りをつけることもできます。"
      },
      {
        question: "どんな用途に向いていますか？",
        answer: "文房具、ガジェット、コスメ・ネイル用品、薬、ケーブル、工具、裁縫道具、推し活グッズ、ボードゲーム、トレカ類、小物ケース内の仕切りなど自由な発想で使ってください。"
      }
    ]
  },
  footer: {
    ariaLabel: "フッターリンク",
    links: {
      contact: "お問い合わせ",
      privacy: "プライバシーポリシー",
      terms: "利用規約",
      "commercial-disclosure": "特定商取引法に基づく表記"
    }
  },
  categories: {
    backLabel: "LPに戻る",
    title: "作りたい形を選ぶ",
    createLabel: (categoryTitle) => `${categoryTitle}をエディターで試す`,
    items: {
      "basic-box": {
        title: "ボックス",
        summary: "深め・大きめの普通の箱"
      },
      "tcg-card-box": {
        title: "TCGカード入れ",
        summary: "デフォルトはポケモンカードサイズ"
      },
      "cutlery-tray": {
        title: "カトラリー",
        summary: "浅型・縦長"
      }
    }
  },
  editor: {
    aboutApp: "このアプリについて",
    backToCategoriesLabel: "カテゴリ選択に戻る",
    hideToolbar: "操作メニューを閉じる",
    showToolbar: "操作メニューを開く",
    checkout: {
      creating: "決済へ移動中...",
      verifying: "支払い確認中...",
      upgrading: "アップグレード中...",
      exporting: "出力中...",
      exportReady: "出力",
      download: "ダウンロード",
      upgrade: "アップグレードして出力",
      reexport: "再出力",
      reexporting: "再出力中...",
      restorePurchase: "購入を復元",
      restoringPurchase: "復元中..."
    },
    restoreDesign: "復元",
    saveDesign: "保存",
    savedDesigns: "保存済み",
    savedDesignsTitle: "保存済みの設計",
    closeSavedDesigns: "保存済みの設計を閉じる",
    noSavedDesigns: "この端末に保存された設計はまだありません。",
    openSavedDesign: "開く",
    openSavedDesignLabel: (name) => `${name}を開く`,
    deleteSavedDesign: "削除",
    deleteSavedDesignLabel: (name) => `${name}を削除`,
    deleteSavedDesignConfirm: (name) => `この端末から「${name}」を削除します。この操作は元に戻せません。削除しますか？`,
    savedDesignLimitWarning: (name) => `保存できる設計は5件までです。続けると、この端末から古い保存「${name}」が削除されます。保存しますか？`,
    sections: {
      size: "サイズ（外寸）",
      basics: "基本",
      add: "追加"
    },
    fields: {
      width: "幅",
      depth: "奥行",
      height: "高さ",
      bottom: "底",
      wallThickness: "壁厚",
      bottomThickness: "底厚",
      cornerRadius: "角丸",
      handleSize: "取手サイズ",
      dividerThickness: "仕切り厚",
      dividerHeight: "仕切り高さ"
    },
    bottomOptions: {
      withBottom: "底あり",
      withoutBottom: "底なし"
    },
    tools: {
      handle: "取手",
      verticalDivider: "縦仕切り",
      horizontalDivider: "横仕切り"
    },
    splitSelection: "左右分割する",
    deleteSelection: "選択中を削除",
    toolHelp: {
      handleDesktop: "モデル上へドラッグして追加。左右端で面を切り替えます。",
      dividerDesktop: "モデル上へドラッグして仕切りを追加します。",
      mobile: "長押しでドラッグし、図面上にドロップ。"
    },
    mobileSelection: {
      dividerAriaLabel: "選択中の仕切り位置",
      handleAriaLabel: "選択中の取手位置",
      face: "面",
      horizontalPosition: "左右位置",
      depthPosition: "前後位置",
      height: "高さ",
      left: "左",
      right: "右",
      back: "奥",
      front: "手前",
      bottom: "下",
      top: "上"
    },
    handleControls: {
      label: "取手",
      none: "なし",
      notch: "切欠き",
      slot: "長丸"
    },
    wallFaceLabels: {
      front: "手前面",
      right: "右面",
      back: "奥面",
      left: "左面"
    },
    addToolAriaLabel: (label) => `${label}をモデルに追加`,
    handleSideDrop: {
      previousFace: "前の面",
      nextFace: "次の面",
      currentFace: (faceLabel) => `正面: ${faceLabel}`
    },
    modelHints: {
      handleDesktop: (faceLabel) => `${faceLabel}へドロップ。左右端で面を切替。`,
      dividerDesktop: "ドロップした位置に仕切りを追加。",
      mobile: "長押しでドラッグし、図面上にドロップ。"
    },
    validation: {
      fixBeforeDownload: "赤い警告が出ている数値を直すとダウンロードできます。",
      range: (min, max, unit) => `${min}-${max}${unit}の範囲で入力してください。`,
      required: "数値を入力してください。"
    }
  },
  contact: {
    title: "お問い合わせ",
    lead: "ハコフィットに関するご質問・ご要望・不具合の報告などがありましたら、以下のメールアドレス宛にご連絡ください。",
    note: "内容を確認のうえ、必要に応じてご連絡いたします。ダウンロードや決済に関するお問い合わせの場合は、発生した操作や表示されたメッセージもあわせてお知らせください。",
    mailSubject: "ハコフィットへのお問い合わせ",
    mailButton: "メールを作成する"
  },
  numberField: {
    decrease: (label) => `${label}を減らす`,
    increase: (label) => `${label}を増やす`
  }
};

const enCopy: AppCopy = {
  brand: {
    name: "Hakofit",
    tagline: "3D print data maker",
    logoLabel: "Hakofit"
  },
  languageSwitch: {
    ariaLabel: "Display language",
    ja: "JA",
    en: "English"
  },
  seo: {
    defaultTitle: "Hakofit | Create custom-fit 3D print data for storage parts",
    defaultDescription: "Hakofit lets you create 3D print data for custom storage boxes, trays, and organizers that fit your drawers, shelves, and cases directly in the browser.",
    contactTitle: "Contact | Hakofit",
    contactDescription: "Contact Hakofit for questions, requests, bug reports, payments, or download support.",
    privacyFallbackDescription: "This page explains how Hakofit handles user information.",
    termsFallbackDescription: "This page sets out the terms and conditions for using Hakofit.",
    commercialDisclosureDescription: "Legal disclosure for Hakofit, including seller information, pricing, payment methods, and cancellation policy.",
    ogImageAlt: "Hakofit service overview image"
  },
  notices: {
    templateLoaded: (templateTitle) => `Loaded the default ${templateTitle} settings.`,
    checkoutUrlMissing: "Could not retrieve the Checkout URL.",
    pendingDesignMissing: "Payment returned, but the design data from before checkout could not be found. Please start again from the app export button.",
    savedDesignMismatch: "Could not verify the saved design data. Please export again.",
    paymentDesignMismatch: "The payment information does not match the design data.",
    paymentSuccess: "Payment verification is complete. The ZIP file with a re-export key has been downloaded.",
    checkoutCanceled: "Checkout was canceled. Your edits have been restored.",
    checkoutSessionMissing: "Checkout Session ID was not found.",
    saveDesignFailed: "Could not temporarily save the design data in this browser.",
    deviceDesignSaved: (name) => `Saved ${name} to this device.`,
    deviceDesignOpened: (name) => `Opened ${name}.`,
    deviceDesignDeleted: (name) => `Deleted ${name}.`,
    deviceDesignMissing: "The saved design was not found.",
    deviceSaveFailed: "Could not save the design to this device.",
    restoreDesignSuccess: "Restored the design from design.json.",
    restoreDesignFileMissing: "Please select design.json.",
    reexportFilesMissing: "To re-export, select both design.json and hakofit-key.json.",
    reexportSuccess: "The re-export key was verified and the same design data was downloaded again.",
    nativeExportSuccess: "The ZIP file is ready. Choose where to save or share it.",
    exportUpgradePurchaseSuccess: "The export upgrade is now active.",
    exportUpgradePurchaseCanceled: "The upgrade purchase was canceled.",
    exportUpgradePurchasePending: "The purchase is pending. Try exporting again after it is approved.",
    exportUpgradeStatusFailed: "Could not check the purchase status. Check your connection and try again.",
    exportUpgradeRestored: "The purchased export upgrade was restored.",
    exportUpgradeRestoreMissing: "No export upgrade purchase was found to restore.",
    entitlementInvalid: "The re-export key signature could not be verified.",
    entitlementExpired: "The re-export key has expired.",
    entitlementDesignTypeMismatch: "The design type in the re-export key does not match.",
    reexportDesignMismatch: "design.json and the re-export key do not match. Select the files from the same ZIP.",
    genericError: "Something went wrong. Please try again later."
  },
  landing: {
    heroDescription: "Turn storage goods that perfectly fit your drawers and shelves into 3D print data with simple steps.",
    cta: "Start in One Click",
    capabilitiesAriaLabel: "Capabilities",
    capabilities: ["No account required", "Free editing and preview", "Print via an outside service"],
    howToTitle: "Create first, export later",
    howToSteps: [
      { title: "1. Create in the editor", summary: "Enter dimensions, move dividers and handles, and check the shape" },
      { title: "2. Export when it fits", summary: "Download 3D print data only for the design you want to use" }
    ],
    useCaseTitle: "Create the shape for your storage space first",
    useCaseBody: "Hakofit is a web app specialized for boxes, trays, and divided storage parts instead of a general-purpose 3D CAD tool.\nTry the size and divider layout before checkout, then export only when the shape works.",
    useCases: [
      {
        title: "Boxes that fit drawers and shelves",
        summary: "Enter width, depth, and height to create storage boxes that fit where off-the-shelf products do not."
      },
      {
        title: "Organize hobbies and daily items",
        summary: "Make boxes for stationery, gadgets, cosmetics, medicine, cables, tools, fan goods, board games, cards, and other small items."
      }
    ],
    pricingTitle: "Editing and preview are free. Export is 550 yen including tax",
    pricingBody: "Editing and 3D preview are free. You pay only when you download the design as 3D print data.",
    pricingItems: [
      { term: "Editing and preview", description: "Free" },
      { term: "Data export", description: "3D print data export fee: 550 yen" },
      { term: "External printing", description: "Print and shipping costs depend on each service" },
      { term: "Delivery", description: "Data delivery only, not a finished product" }
    ],
    faqTitle: "FAQ",
    faqs: [
      {
        question: "Does it cost anything just to try it?",
        answer: "No. Editing dimensions, adjusting dividers, and checking the 3D preview are free. You pay only when you download 3D print data."
      },
      {
        question: "Will I receive a finished storage product?",
        answer: "No. Hakofit does not sell or ship finished products. It creates 3D print data for storage parts."
      },
      {
        question: "Can I use it without owning a 3D printer?",
        answer: "Yes. You can send the exported 3D print data to an external 3D printing service such as DMM.make or JLC3DP."
      },
      {
        question: "What kind of data can I export?",
        answer: "You can create box-shaped parts in custom sizes and add handles or dividers."
      },
      {
        question: "What use cases is Hakofit good for?",
        answer: "Use it for stationery, gadgets, cosmetics and nail supplies, medicine, cables, tools, sewing kits, fan goods, board games, trading cards, small-case dividers, and similar storage ideas."
      }
    ]
  },
  footer: {
    ariaLabel: "Footer links",
    links: {
      contact: "Contact",
      privacy: "Privacy Policy",
      terms: "Terms of Use",
      "commercial-disclosure": "Legal Disclosure"
    }
  },
  categories: {
    backLabel: "Back to landing page",
    title: "Choose a shape to make",
    createLabel: (categoryTitle) => `Try ${categoryTitle} in the editor`,
    items: {
      "basic-box": {
        title: "Box",
        summary: "A deep, roomy standard box"
      },
      "tcg-card-box": {
        title: "TCG card box",
        summary: "Defaults to Pokemon card size"
      },
      "cutlery-tray": {
        title: "Cutlery tray",
        summary: "Shallow and long"
      }
    }
  },
  editor: {
    aboutApp: "About this app",
    backToCategoriesLabel: "Back to category selection",
    hideToolbar: "Close actions menu",
    showToolbar: "Open actions menu",
    checkout: {
      creating: "Opening checkout...",
      verifying: "Verifying payment...",
      upgrading: "Upgrading...",
      exporting: "Exporting...",
      exportReady: "Export",
      download: "Download",
      upgrade: "Upgrade and export",
      reexport: "Re-export",
      reexporting: "Re-exporting...",
      restorePurchase: "Restore purchase",
      restoringPurchase: "Restoring..."
    },
    restoreDesign: "Restore",
    saveDesign: "Save",
    savedDesigns: "Saved",
    savedDesignsTitle: "Saved designs",
    closeSavedDesigns: "Close saved designs",
    noSavedDesigns: "No designs have been saved on this device yet.",
    openSavedDesign: "Open",
    openSavedDesignLabel: (name) => `Open ${name}`,
    deleteSavedDesign: "Delete",
    deleteSavedDesignLabel: (name) => `Delete ${name}`,
    deleteSavedDesignConfirm: (name) => `Delete "${name}" from this device? This cannot be undone.`,
    savedDesignLimitWarning: (name) => `You can save up to 5 designs. Continuing will remove the oldest saved design, "${name}", from this device. Save anyway?`,
    sections: {
      size: "Size",
      basics: "Basics",
      add: "Add"
    },
    fields: {
      width: "Width",
      depth: "Depth",
      height: "Height",
      bottom: "Bottom",
      wallThickness: "Wall",
      bottomThickness: "Base",
      cornerRadius: "Corner",
      handleSize: "Handle size",
      dividerThickness: "Divider wall",
      dividerHeight: "Divider height"
    },
    bottomOptions: {
      withBottom: "With base",
      withoutBottom: "Open base"
    },
    tools: {
      handle: "Handle",
      verticalDivider: "Vertical",
      horizontalDivider: "Horizontal"
    },
    splitSelection: "Split selected divider",
    deleteSelection: "Delete selection",
    toolHelp: {
      handleDesktop: "Drag onto the model to add a handle. Use the left and right edges to switch faces.",
      dividerDesktop: "Drag onto the model to add a divider.",
      mobile: "Long-press, drag, and drop onto the plan."
    },
    mobileSelection: {
      dividerAriaLabel: "Selected divider position",
      handleAriaLabel: "Selected handle position",
      face: "Face",
      horizontalPosition: "Horizontal position",
      depthPosition: "Depth position",
      height: "Height",
      left: "Left",
      right: "Right",
      back: "Back",
      front: "Front",
      bottom: "Lower",
      top: "Upper"
    },
    handleControls: {
      label: "Handle",
      none: "None",
      notch: "Notch",
      slot: "Slot"
    },
    wallFaceLabels: {
      front: "Front side",
      right: "Right side",
      back: "Back side",
      left: "Left side"
    },
    addToolAriaLabel: (label) => `Add ${label} to the model`,
    handleSideDrop: {
      previousFace: "Previous face",
      nextFace: "Next face",
      currentFace: (faceLabel) => `Face: ${faceLabel}`
    },
    modelHints: {
      handleDesktop: (faceLabel) => `Drop on ${faceLabel}. Use the left and right edges to switch faces.`,
      dividerDesktop: "Add a divider at the drop position.",
      mobile: "Long-press, drag, and drop onto the plan."
    },
    validation: {
      fixBeforeDownload: "Fix the fields marked in red before downloading.",
      range: (min, max, unit) => `Enter a value from ${min}-${max}${unit}.`,
      required: "Enter a number."
    }
  },
  contact: {
    title: "Contact",
    lead: "For questions, requests, or bug reports about Hakofit, please contact the email address below.",
    note: "We will review your message and reply if needed. For download or payment issues, include the operation you performed and any message that appeared.",
    mailSubject: "Inquiry about Hakofit",
    mailButton: "Create email"
  },
  numberField: {
    decrease: (label) => `Decrease ${label}`,
    increase: (label) => `Increase ${label}`
  }
};

const englishLegalPages: Record<StaticPageSlug, LegalPageContent> = {
  privacy: {
    slug: "privacy",
    title: "Privacy Policy",
    lead: "This policy explains how Hakofit handles user information.",
    effectiveDate: "Effective date: April 25, 2026",
    sections: [
      {
        heading: "1. Business Information",
        items: [
          "Business operator: Yusuke Kamiya, sole proprietor",
          "Service name: Hakofit",
          "Contact: the contact page in this service",
          "Address and telephone number: omitted under the proviso of Article 11 of Japan's Specified Commercial Transactions Act. If disclosure is requested through the contact page, we will disclose them without delay in writing or by email."
        ]
      },
      {
        heading: "2. Scope",
        paragraphs: [
          "This Privacy Policy applies to user information acquired by the operator through this service and this website."
        ]
      },
      {
        heading: "3. Information We Collect",
        paragraphs: [
          "The operator may mainly collect the following information when providing this service."
        ],
        items: [
          "Information collected automatically during use: IP address, browser information, operating system, device information, access date and time, operation history, and identifiers such as cookies and localStorage.",
          "Design and export information: design data hashes, design type, and metadata needed before and after payment. 3D models and design data are generally generated inside the user's browser, but may be acquired when voluntarily provided by the user for inquiries or bug investigation.",
          "Payment-related information: full credit card numbers are managed by payment processors such as Stripe, and the operator does not store or view the full card number. The operator may collect Checkout Session IDs, payment amounts, payment dates and times, payment status, and similar information.",
          "Information collected during inquiries: inquiry contents, contact details needed for replies, screenshots, usage environment, and similar information.",
          "Log and diagnostic information: application error logs, performance information, and security-related logs."
        ]
      },
      {
        heading: "4. Purposes of Use",
        items: [
          "To provide and operate this service, confirm payments, export 3D print data, and issue re-export keys.",
          "To investigate bugs, improve quality, improve features, and analyze usage.",
          "To respond to inquiries and send important notices or notices of changes to terms and policies.",
          "To detect and prevent unauthorized use, fraudulent payments, and security issues.",
          "To respond to requests from laws, regulations, or public authorities."
        ]
      },
      {
        heading: "5. Use of Cookies and Similar Technologies",
        items: [
          "This service may use cookies, localStorage, and similar technologies to temporarily save design data before payment, restore it after payment, improve convenience, and analyze access.",
          "Users can restrict cookies and similar technologies through browser settings, but in that case downloads after payment or some functions may not work properly.",
          "If analytics services are used, the outline of collected information will be disclosed through notices on this website or revisions to this policy."
        ]
      },
      {
        heading: "6. Third-Party Disclosure",
        items: [
          "Except with the user's consent, when required by law, when necessary to protect life, body, or property, or where otherwise permitted by law, the operator will not provide acquired personal information to third parties.",
          "Information processed statistically or otherwise modified so that individuals cannot be identified may be used for service improvement or public materials."
        ]
      },
      {
        heading: "7. Entrustment",
        paragraphs: [
          "The operator may entrust all or part of personal information handling to external providers for service operation, payment processing, hosting, inquiry handling, error log collection, and similar purposes. In such cases, the operator will appropriately supervise the entrusted providers."
        ],
        items: [
          "Payment processors that provide credit card payments and similar services.",
          "Cloud infrastructure providers that provide application hosting, delivery, and security features.",
          "Service providers for error log collection, analytics, inquiry handling, and similar functions."
        ]
      },
      {
        heading: "8. Security Measures",
        items: [
          "Communication encryption such as HTTPS/SSL.",
          "Appropriate management of secret keys, API keys, and authentication information.",
          "Access permission management.",
          "Technical measures to prevent unauthorized access.",
          "Rules for handling personal information."
        ]
      },
      {
        heading: "9. Retention Period",
        paragraphs: [
          "The operator retains acquired information within the scope necessary to achieve the purposes of use. Some information may be retained for a certain period as necessary for legal obligations, payment confirmation, dispute handling, and prevention of unauthorized use."
        ]
      },
      {
        heading: "10. Requests for Disclosure, Correction, and Suspension of Use",
        items: [
          "Users may request disclosure, correction, addition, deletion, suspension of use, and similar actions regarding their personal information held by the operator.",
          "To make a request, contact us through the contact page in this service. Additional information may be requested to verify identity.",
          "The operator will respond promptly within a reasonable scope in accordance with laws and regulations. However, requests may not be fulfilled where the operator is not legally required to disclose information, where the request is excessive, or in similar cases."
        ]
      },
      {
        heading: "11. Minor Users",
        paragraphs: [
          "If a minor uses this service, they must use it with the consent of a parent or other legal representative."
        ]
      },
      {
        heading: "12. Changes to This Privacy Policy",
        paragraphs: [
          "The operator may change this Privacy Policy as needed. Important changes will be notified by posting on this website or by another appropriate method."
        ]
      },
      {
        heading: "13. Contact",
        paragraphs: [
          "For inquiries about this Privacy Policy or the handling of personal information, or for requests such as disclosure, please contact us through the contact page in this service."
        ]
      }
    ]
  },
  terms: {
    slug: "terms",
    title: "Terms of Use",
    lead: "These Terms of Use set out the conditions for providing and using Hakofit and the rights and obligations related to its use.",
    effectiveDate: "Effective date: April 25, 2026",
    sections: [
      {
        heading: "Article 1. Application",
        items: [
          "These Terms apply to all relationships related to the provision and use of this service.",
          "The operator may establish separate rules, guidelines, notes, and similar provisions regarding this service. These form part of these Terms."
        ]
      },
      {
        heading: "Article 2. Definitions",
        items: [
          "\"This service\" means the browser-based service provided as Hakofit for creating and exporting 3D print data for storage boxes, trays, dividers, and similar items.",
          "\"User\" means any person who accesses or uses this service.",
          "\"Exported data\" means 3D print data, design data, README files, re-export keys, and similar data generated through this service.",
          "\"External print service\" means DMM.make, JLC3DP, or another 3D print service used by the user to manufacture exported data, or the user's own 3D printer."
        ]
      },
      {
        heading: "Article 3. Agreement to the Terms",
        items: [
          "Users may use this service only if they agree to these Terms.",
          "A user is deemed to have agreed to these Terms when actually using this service.",
          "If a minor uses this service, they must use it after obtaining the consent of a parent or other legal representative."
        ]
      },
      {
        heading: "Article 4. Service Contents",
        items: [
          "This service is a browser service that generates 3D print data for storage boxes, trays, dividers, and similar items based on dimensions and options entered by the user.",
          "This service does not sell or ship finished storage products.",
          "Users may use generated exported data with their own 3D printer or submit it to an external print service for manufacturing.",
          "This service does not guarantee manufacturing fees, shipping fees, delivery times, materials, manufacturability, or finished quality of external print services."
        ]
      },
      {
        heading: "Article 5. Fees and Payment Method",
        items: [
          "Paid export from this service requires a data creation fee. The current data creation fee is 550 yen including tax. If the price changes, the amount shown on this website or the checkout screen takes precedence.",
          "The data creation fee is consideration for 3D print data generation and design data creation, and does not include manufacturing fees, shipping fees, taxes, material costs, or other costs charged by external print services.",
          "Users shall pay the data creation fee by the payment method designated by the operator, such as credit card payment.",
          "Payment processing is handled through payment processors such as Stripe."
        ]
      },
      {
        heading: "Article 6. Exported Data and Re-Export",
        items: [
          "After payment is completed, users can download exported data as a ZIP file.",
          "Exported data mainly includes 3D print data, design data, a re-export key, and upload guidance.",
          "The re-export key is used to confirm re-export of the same design data for a certain period. Exporting a different design after changing dimensions, dividers, handles, or other settings requires a new payment. If the re-export key or design data is lost, automatic restoration may not be possible.",
          "The operator is not responsible for disadvantages caused by browser cache deletion, device changes, loss of downloaded files, or similar reasons."
        ]
      },
      {
        heading: "Article 7. 3D Printing and Suitability",
        items: [
          "Before manufacturing, users shall confirm dimensions, wall thickness, bottom thickness, dividers, cutouts, intended use, materials, manufacturing method, and submission requirements of external print services at their own responsibility.",
          "This service does not guarantee that exported data can be manufactured by every printer, material, or external print service, or that manufactured items will perfectly fit the user's storage space or purpose.",
          "Users are responsible for confirming strength, heat resistance, chemical resistance, suitability for food contact, safety for children and pets, and similar matters of manufactured items.",
          "Contracts, payments, shipping, returns, and inquiries with external print services are handled between the user and the relevant external print service."
        ]
      },
      {
        heading: "Article 8. Prohibited Acts",
        items: [
          "Acts that violate laws, regulations, or public order and morals.",
          "Acts related to criminal acts or creation of dangerous goods, weapons, or illegal goods.",
          "Acts that infringe intellectual property rights, portrait rights, privacy rights, reputation rights, or other rights or interests of others.",
          "Analyzing, modifying, or reverse engineering this service.",
          "Placing excessive load on the servers, networks, or payment functions of this service.",
          "Unauthorized access, fraudulent payments, tampering with re-export keys, or attempts to do so.",
          "Other acts the operator deems inappropriate."
        ]
      },
      {
        heading: "Article 9. Intellectual Property Rights and Use of Exported Data",
        items: [
          "Intellectual property rights related to this website and this service, including programs, designs, logos, text, and images, belong to the operator or legitimate rights holders.",
          "Users may save, edit, 3D print, and submit to external print services exported data obtained through proper payment for their own use.",
          "Users must not use the templates, export logic, UI, or exported data of this service to build competing services, redistribute them, or sell them without permission from the operator.",
          "If information entered by the user regarding dimensions or use includes third-party rights, the user is responsible for obtaining necessary rights clearance."
        ]
      },
      {
        heading: "Article 10. Changes, Suspension, and Termination of the Service",
        items: [
          "The operator may change, suspend, or terminate all or part of this service due to maintenance, incident response, suspension of external services, security needs, or other unavoidable reasons.",
          "Except in cases of willful misconduct or gross negligence by the operator, the operator is not responsible for damages suffered by users due to changes, suspension, or termination under this Article."
        ]
      },
      {
        heading: "Article 11. Disclaimer",
        items: [
          "This service is provided as is. The operator does not warrant that this service is free from factual or legal defects.",
          "The operator does not guarantee completeness, accuracy, manufacturability, fitness for a particular purpose, acceptance by external print services, or quality or safety of manufactured items regarding exported data.",
          "The operator is not responsible for cases where this service does not operate properly due to the user's device, communication environment, browser, WebGL compatibility, download settings, or similar causes.",
          "Even where the operator is responsible, the scope of the operator's liability for damages is limited to the amount paid by the user to the operator in the relevant transaction, except in cases of willful misconduct or gross negligence by the operator."
        ]
      },
      {
        heading: "Article 12. Refunds",
        paragraphs: [
          "Because exported data is digital content, returns, cancellations, and refunds after payment completion are generally not accepted. However, this does not apply where the operator deems it necessary, such as duplicate payment or inability to obtain exported data after payment completion, or where required by law."
        ]
      },
      {
        heading: "Article 13. Handling of Personal Information",
        paragraphs: [
          "Handling of user information in this service is governed by the separately established Privacy Policy."
        ]
      },
      {
        heading: "Article 14. Changes to the Terms",
        paragraphs: [
          "The operator may change these Terms when deemed necessary. Changed Terms take effect when posted on this website."
        ]
      },
      {
        heading: "Article 15. Notices and Communications",
        paragraphs: [
          "Notices from the operator to users regarding this service will be made by posting on this website, sending email, or another method the operator considers appropriate. Communications from users to the operator shall be made through the contact page in this service or to the contact information designated by the operator."
        ]
      },
      {
        heading: "Article 16. Governing Law and Jurisdiction",
        items: [
          "These Terms are governed by the laws of Japan.",
          "If a dispute arises between the operator and a user regarding this service, the district court or summary court with jurisdiction over the operator's address shall be the exclusive agreed court of first instance."
        ]
      }
    ]
  },
  "commercial-disclosure": {
    slug: "commercial-disclosure",
    title: "Legal Disclosure",
    lead: "The following disclosures apply to mail-order transactions.",
    effectiveDate: "Effective date: April 25, 2026",
    sections: [
      {
        heading: "Disclosure Items",
        rows: [
          { label: "Seller", value: "Yusuke Kamiya, sole proprietor" },
          { label: "Operations manager", value: "Yusuke Kamiya" },
          { label: "Address and telephone number", value: "Omitted under the proviso of Article 11 of Japan's Specified Commercial Transactions Act. If requested, they will be provided without delay. Please contact us through the contact page in this service if needed." },
          { label: "Email address", value: "contact@hakofit.com" },
          { label: "Selling price", value: "Data export fee: 550 yen including tax. If the price changes, the amount shown on the purchase procedure screen takes precedence." },
          { label: "Additional fees", value: "Internet connection fees and communication fees are borne by the user. When using an external print service, manufacturing fees, shipping fees, taxes, material costs, and other costs are determined by each external print service and borne by the user." },
          { label: "Payment method", value: "Credit card payment and similar methods through Stripe." },
          { label: "Payment timing", value: "Payment is processed during the purchase procedure." },
          { label: "Delivery timing", value: "After payment completion and payment verification, the ZIP file becomes downloadable in the browser." },
          { label: "Returns, exchanges, cancellations", value: "Due to the nature of digital content, returns, exchanges, cancellations, and refunds after payment completion are generally not accepted. However, this does not apply where the operator deems it necessary, such as duplicate payment or inability to obtain exported data after payment completion, or where required by law." },
          { label: "Operating environment", value: "Recommended browsers: latest versions of Google Chrome, Safari, Firefox, and Microsoft Edge. A WebGL-compatible environment is required for 3D preview." },
          { label: "External print services", value: "This service creates 3D print data and does not provide or guarantee manufacturing services, delivery, material quality, delivery dates, or manufacturability. Contracts with external print services are formed between the user and the relevant provider." }
        ]
      }
    ]
  }
};

export const copyByLanguage: Record<Language, AppCopy> = {
  ja: jaCopy,
  en: enCopy
};

export const localizedLegalPages: Record<Language, Record<StaticPageSlug, LegalPageContent>> = {
  ja: legalPages,
  en: englishLegalPages
};
